import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ShareNetwork, Copy, EnvelopeSimple, DeviceMobile, CalendarBlank, CalendarCheck, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDayText, formatWeekText, openDayPdf, openWeekPdf } from "@/lib/programText";
import { api, mondayOf, addDays, todayISO, DAY_NAMES } from "@/lib/api";

// Convert an ISO date to its 0..6 slot within its Monday-based week.
function _daySlot(iso) {
  const monday = mondayOf(iso);
  const m = new Date(monday + "T00:00:00");
  const d = new Date(iso + "T00:00:00");
  return Math.round((d - m) / 86400000);
}

/**
 * Share the day's or the whole week's programming as plain text — over
 * SMS, email, clipboard, or PDF.
 *
 * By default targets the currently-viewed program + active day. The user
 * can override with an in-menu date picker that loads any other week
 * on-demand — no navigation needed.
 */
export default function ShareProgramButton({ program: currentProgram, dayIdx: activeDayIdx, athleteName, athleteId, dayDates }) {
  const [pickedDate, setPickedDate] = useState("");
  const [remoteProgram, setRemoteProgram] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!currentProgram) return null;

  // If a date is picked, load the program for that week and use it as the
  // share source. Otherwise fall back to the currently-viewed program.
  const targetAthleteId = athleteId || currentProgram.athlete_id;
  const targetProgram = pickedDate ? remoteProgram : currentProgram;
  const targetDayIdx = pickedDate
    ? _daySlot(pickedDate)
    : activeDayIdx;

  const loadForDate = async (iso) => {
    setPickedDate(iso);
    if (!iso) { setRemoteProgram(null); return; }
    const monday = mondayOf(iso);
    if (monday === currentProgram.week_start) {
      // Same week as the one on screen — reuse it, no fetch.
      setRemoteProgram(currentProgram);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/programs/by-week/${targetAthleteId}/${monday}`);
      setRemoteProgram(data);
    } catch {
      toast.error("Could not load that week — try again.");
      setRemoteProgram(null);
    } finally {
      setLoading(false);
    }
  };

  const dayLabel = targetProgram && targetDayIdx != null
    ? `day (${addDays(targetProgram.week_start, targetDayIdx)})`
    : "day";
  const weekLabel = targetProgram ? `week of ${targetProgram.week_start}` : "week";

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${what} to clipboard`);
    } catch {
      toast.error("Clipboard blocked — try email/SMS instead");
    }
  };

  const openMail = (subject, body) => {
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const openSms = (body) => {
    // sms: URL scheme — works on iOS + Android. Body param is `&body=` on
    // iOS 8+ and Android; some Android builds still expect `?body=`, so we
    // send both semicolon-safely by URL-encoding.
    const url = `sms:?&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  const nativeShare = async (body, title) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: body });
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const doDay = async (kind) => {
    if (!targetProgram) { toast.error("Pick a valid date first."); return; }
    const text = formatDayText(targetProgram, targetDayIdx);
    const subj = `Training — ${dayLabel}${athleteName ? ` · ${athleteName}` : ""}`;
    if (kind === "copy") return copy(text, "day");
    if (kind === "email") return openMail(subj, text);
    if (kind === "sms") return openSms(text);
    if (kind === "pdf") return openDayPdf(targetProgram, targetDayIdx, athleteName);
    if (kind === "share") {
      const ok = await nativeShare(text, subj);
      if (!ok) copy(text, "day");
    }
  };

  const doWeek = async (kind) => {
    if (!targetProgram) { toast.error("Pick a valid date first."); return; }
    const text = formatWeekText(targetProgram, athleteName);
    const subj = `Training — ${weekLabel}${athleteName ? ` · ${athleteName}` : ""}`;
    if (kind === "copy") return copy(text, "week");
    if (kind === "email") return openMail(subj, text);
    if (kind === "sms") return openSms(text);
    if (kind === "pdf") return openWeekPdf(targetProgram, athleteName);
    if (kind === "share") {
      const ok = await nativeShare(text, subj);
      if (!ok) copy(text, "week");
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className="rounded-xl gap-2"
          data-testid="share-program-button"
          title="Share this day or week"
        >
          <ShareNetwork size={14} weight="bold" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2" data-testid="share-program-menu">
        <div className="px-2 pt-1 pb-2 border-b border-line">
          <label className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-3 flex items-center gap-1.5">
            <CalendarBlank size={11} weight="bold" /> Date
          </label>
          <input
            type="date"
            value={pickedDate}
            onChange={(e) => loadForDate(e.target.value)}
            className="mt-1 h-8 w-full rounded-md border border-line bg-white px-2 text-sm font-data"
            data-testid="share-date-picker"
          />
          <div className="mt-1 flex items-center justify-between text-[11px]">
            <span className="text-ink-3">
              {pickedDate
                ? (loading
                    ? "Loading…"
                    : targetProgram
                      ? `${DAY_NAMES[targetDayIdx]} · week of ${targetProgram.week_start}`
                      : "No program for that week")
                : "Uses the week on screen"}
            </span>
            {pickedDate && (
              <button
                type="button"
                onClick={() => { setPickedDate(""); setRemoteProgram(null); }}
                className="text-terracotta hover:underline"
                data-testid="share-date-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-3 px-2 pt-2 pb-1 flex items-center gap-1.5">
          <CalendarCheck size={11} weight="bold" /> This day
        </div>
        {hasNativeShare && (
          <button type="button" onClick={() => doDay("share")}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                  data-testid="share-day-native">
            <DeviceMobile size={14} /><span className="flex-1">Share sheet…</span>
          </button>
        )}
        <button type="button" onClick={() => doDay("sms")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-day-sms">
          <DeviceMobile size={14} /><span className="flex-1">Text (SMS)</span>
        </button>
        <button type="button" onClick={() => doDay("email")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-day-email">
          <EnvelopeSimple size={14} /><span className="flex-1">Email…</span>
        </button>
        <button type="button" onClick={() => doDay("copy")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-day-copy">
          <Copy size={14} /><span className="flex-1">Copy to clipboard</span>
        </button>
        <button type="button" onClick={() => doDay("pdf")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-day-pdf">
          <FilePdf size={14} weight="bold" /><span className="flex-1">Download PDF</span>
        </button>

        <div className="border-t border-line my-1" />
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-3 px-2 pt-1 pb-1 flex items-center gap-1.5">
          <CalendarBlank size={11} weight="bold" /> Whole week
        </div>
        {hasNativeShare && (
          <button type="button" onClick={() => doWeek("share")}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                  data-testid="share-week-native">
            <DeviceMobile size={14} /><span className="flex-1">Share sheet…</span>
          </button>
        )}
        <button type="button" onClick={() => doWeek("sms")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-week-sms">
          <DeviceMobile size={14} /><span className="flex-1">Text (SMS)</span>
        </button>
        <button type="button" onClick={() => doWeek("email")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-week-email">
          <EnvelopeSimple size={14} /><span className="flex-1">Email…</span>
        </button>
        <button type="button" onClick={() => doWeek("copy")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-week-copy">
          <Copy size={14} /><span className="flex-1">Copy to clipboard</span>
        </button>
        <button type="button" onClick={() => doWeek("pdf")}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm hover:bg-muted2"
                data-testid="share-week-pdf">
          <FilePdf size={14} weight="bold" /><span className="flex-1">Download PDF</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
